import { GOOGLE_ANALYTICS_SCOPES } from 'src/context/api/google/api';
import * as yup from 'yup';

export interface CountryRegionMappingInput {
    region: string;
    countries: string[];
}

export interface CampaignRegionMapping {
    regex: string[];
    region: string;
}

export interface GadsAccountRegionMapping {
    account_id: string;
    region: string;
}

export interface GscCountrySiteUrlMapping {
    countries: string[];
    url: string;
}

export interface WorkspaceFormData {
    general: {
        name: string;
        url?: string;
        friendlyDomains?: string[];
        template?: string;
        averageCart?: number;
        currency?: string;
        custom?: boolean;
        bqTable?: string;
        shoppingIncluded?: boolean;
        platforms: string[];
    };
    display: {
        showSavings?: boolean;
        brandInfringement?: boolean;
    };
    integrations?: {
        email: string;
        scopes: GOOGLE_ANALYTICS_SCOPES[];
    }[];
    dataConfig: {
        nonbrand?: boolean;
        selected_countries?: string[];
        branded_keywords?: string[];
        excluded_keywords?: string[];
        country_region_mapping?: CountryRegionMappingInput[];
        mapping_type?: 'accounts' | 'campaign_names';
        google_ads?: {
            use_region_mapping?: boolean;
            branded_search_campaigns?: string[];
            branded_shopping_campaigns?: string[];
            account_region_mapping?: GadsAccountRegionMapping[];
            campaign_region_mapping?: CampaignRegionMapping[];
            conversion_actions?: string[];
            exclude_non_branded?: boolean;
            customer_id?: {
                id: string;
                name: string;
            }[];
        };
        google_analytics?: {
            property_id?: {
                ga4_property_id: string;
                ga4_property_name: string;
            }[];
            conversion_events?: string[];
        };
        google_search_console?: {
            site_url?: string[];
            country_site_url_mapping?: GscCountrySiteUrlMapping[];
        };
    };
    scraping?: any[];
}

export type StepKey = 'information' | 'settings' | 'scraping' | 'validation';

export const validationSchema = yup.object().shape({
    general: yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be less than 255 characters'),
        url: yup.string().url('Must be a valid URL').max(255, 'URL must be less than 255 characters'),
        friendlyDomains: yup.array().of(yup.string().required('Friendly domain is required')),
        template: yup.string().max(255, 'Template must be less than 255 characters'),
        averageCart: yup.number().min(0, 'Average cart must be positive'),
        currency: yup.string().max(255, 'Currency must be less than 255 characters'),
        custom: yup.boolean(),
        bqTable: yup.string().max(255, 'BigQuery table must be less than 255 characters'),
        shoppingIncluded: yup.boolean(),
        platforms: yup.array().of(yup.string().oneOf(['Google', 'Bing'])).min(1, 'At least one platform is required'),
    }),
    display: yup.object().shape({
        showSavings: yup.boolean(),
        brandInfringement: yup.boolean(),
    }),
    dataConfig: yup.object().shape({
        selected_countries: yup.array().of(yup.string().required('Country is required')),
        branded_keywords: yup.array().of(yup.string().required('Keyword is required')),
        excluded_keywords: yup.array().of(yup.string().required('Keyword is required')),
        country_region_mapping: yup.array().of(
            yup.object().shape({
                region: yup.string().required('Region is required'),
                countries: yup.array().of(yup.string()).required(),
            })
        ),
        mapping_type: yup
            .mixed<'accounts' | 'campaign_names'>()
            .oneOf(['accounts', 'campaign_names'])
            .required('Mapping type is required'),
        google_ads: yup.object().shape({
            branded_search_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            branded_shopping_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            account_region_mapping: yup.array().of(
                yup.object().shape({
                    account_id: yup.string().required('Account is required'),
                    region: yup.string().required('Region is required'),
                })
            ),
            campaign_region_mapping: yup.array().of(
                yup.object().shape({
                    regex: yup.array().of(yup.string().required('Regex is required')),
                    region: yup.string().required('Region is required'),
                })
            ),
            conversion_actions: yup.array().of(yup.string().required('Conversion action is required')),
            exclude_non_branded: yup.boolean(),
            customer_id: yup.array().of(yup.object().shape({
                id: yup.string().required('Customer ID is required'),
                name: yup.string().required('Customer name is required'),
            })),
        }),
        google_search_console: yup.object().shape({
            country_site_url_mapping: yup.array().of(
                yup.object().shape({
                    countries: yup.array().of(yup.string().required('Country is required')),
                    url: yup.string().required('URL is required'),
                })
            ),
        }),
        google_analytics: yup.object().shape({
            property_id: yup.array().of(yup.object().shape({
                ga4_property_id: yup.string().required('Property ID is required'),
                ga4_property_name: yup.string().required('Property name is required'),
            })),
            conversion_events: yup.array().of(yup.string().required('Conversion event is required')),
        }),
    }),
});

export const emptyValues: WorkspaceFormData = {
    general: {
        name: '',
        url: '',
        friendlyDomains: [],
        template: '',
        averageCart: 0,
        currency: 'usd',
        custom: false,
        bqTable: '',
        shoppingIncluded: false,
        platforms: [],
    },
    display: {
        showSavings: true,
        brandInfringement: false,
    },
    dataConfig: {
        selected_countries: [],
        branded_keywords: [],
        excluded_keywords: [],
        country_region_mapping: [],
        mapping_type: undefined,
        google_ads: {
            use_region_mapping: false,
        },
        google_analytics: {},
        google_search_console: {},
    },
    scraping: [],
};


